import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Empty, Typography, Button, Space, Modal } from 'antd';
import moment from 'moment';
import styled from 'styled-components';
import RCTable from 'components/custom-table/RCTable';
import { GreenTickIconSVG, RedTickIconSVG, BluefileIconSVG, BlackfileIconSVG } from 'utils/Svg';

const { Text } = Typography;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
		padding: 0;
	}
	.ant-modal-body {
		padding: 24px;
		text-align: center;
	}
`;

const ModalTitle = styled.div`
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 16px;
	color: #000;
`;

const ModalMessage = styled.div`
	font-size: 14px;
	color: #666;
	margin-bottom: 24px;
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 12px;
	justify-content: center;
`;

function PaymentQueueTab({
	tableDatas,
	togglePaginate,
	dispatchAction,
	handleApprovePayment,
	handleRejectPayment,
	handleViewInvoice,
}) {
	const [rejectModalVisible, setRejectModalVisible] = useState(false);
	const [selectedRecord, setSelectedRecord] = useState(null);

	const showRejectModal = (record) => {
		setSelectedRecord(record);
		setRejectModalVisible(true);
	};

	const handleConfirmReject = () => {
		if (selectedRecord) {
			handleRejectPayment(selectedRecord);
		}
		setRejectModalVisible(false);
		setSelectedRecord(null);
	};

	const handleCancelReject = () => {
		setRejectModalVisible(false);
		setSelectedRecord(null);
	};
	const columns = [
		{
			title: 'Trip ID',
			dataIndex: 'trip_id',
			key: 'trip_id',
			width: 90,
			render: (trip_id) => <Text overFlow="hidden">{trip_id || '-'}</Text>,
		},
		{
			title: 'Req ID',
			dataIndex: 'adhoc_request_id',
			key: 'adhoc_request_id',
			width: 100,
		},
		{
			title: 'Request Type',
			dataIndex: 'request_type',
			key: 'request_type',
			width: 120,
		},
		{
			title: 'Business Unit',
			dataIndex: 'business_unit_name',
			key: 'business_unit_name',
			width: 130,
		},
		{
			title: 'Party Name',
			dataIndex: 'party_name',
			key: 'party_name',
			width: 120,
		},
		{
			title: 'Request Date',
			dataIndex: 'request_date',
			key: 'request_date',
			width: 150,
			render: (date) => {
				if (!date) return '-';
				return moment(date).add(5, 'hours').add(30, 'minutes').format('DD/MM/YYYY h:mm A');
			},
		},
		{
			title: 'Request value',
			dataIndex: 'request_value',
			key: 'request_value',
			width: 130,
			render: (value) => (value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-'),
		},
		{
			title: 'Payable Value',
			dataIndex: 'paid_amount',
			key: 'paid_amount',
			width: 130,
			render: (value) => (value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-'),
		},
		{
			title: 'Action',
			dataIndex: 'action',
			key: 'action',
			width: 120,
			render: (_, record) => (
				<Space>
					<Button
						type="text"
						shape="circle"
						icon={<GreenTickIconSVG />}
						size="small"
						onClick={() => handleApprovePayment(record)}
					/>
					<Button
						type="text"
						shape="circle"
						icon={<RedTickIconSVG />}
						size="small"
						onClick={() => showRejectModal(record)}
					/>
					<Button
						type="text"
						icon={
							record.vendor_proforma_invoice_number !== null ? (
								<BluefileIconSVG />
							) : (
								<BlackfileIconSVG />
							)
						}
						size="small"
						onClick={() => handleViewInvoice(record)}
					/>
				</Space>
			),
		},
	];

	return (
		<>
			<div style={{ marginTop: 16 }}>
				<RCTable
					rowKey="id"
					tableDatas={tableDatas}
					togglePaginate={togglePaginate}
					dispatchAction={dispatchAction}
					columns={columns}
					scroll={{ x: true }}
					locale={{
						emptyText: (
							<Empty
								description={<Text type="secondary">No payments in queue</Text>}
							/>
						),
					}}
				/>
			</div>

			<StyledModal
				open={rejectModalVisible}
				onCancel={handleCancelReject}
				footer={null}
				centered
				width={400}
				closable={false}
			>
				<ModalTitle>Reject Payment</ModalTitle>
				<ModalMessage>
					Are you sure you want to reject this payment request?
					<br />
					This action cannot be undone.
				</ModalMessage>
				<ButtonContainer>
					<Button onClick={handleCancelReject}>Cancel</Button>
					<Button type="primary" danger onClick={handleConfirmReject}>
						Yes, Reject
					</Button>
				</ButtonContainer>
			</StyledModal>
		</>
	);
}

PaymentQueueTab.propTypes = {
	tableDatas: PropTypes.array.isRequired,
	togglePaginate: PropTypes.array.isRequired,
	dispatchAction: PropTypes.func.isRequired,
	handleApprovePayment: PropTypes.func.isRequired,
	handleRejectPayment: PropTypes.func.isRequired,
	handleViewInvoice: PropTypes.func.isRequired,
};

export default PaymentQueueTab;
